import * as React from 'react';
import _ from 'lodash';
import _flow from 'lodash/fp/flow';
import _filter from 'lodash/fp/filter';
import _map from 'lodash/fp/each';

import PropTypes from 'prop-types';
import { log } from 'limuirs-utils';
import { limuirsComponent } from 'limuirs-components';
import { dimensionsType, imageType, videoType } from '../../types';
import ImageSlider from '../media/ImageSlider';

/**
 * Display a list of images associated with a message.
 *
 * @author Dolan Halbrook
 */
class MessageImages extends React.PureComponent {
  static propTypes = {
    message: PropTypes.shape({
      images: PropTypes.shape({
        items: PropTypes.arrayOf(imageType),
        size: PropTypes.number
      }),
      videos: PropTypes.shape({
        items: PropTypes.arrayOf(videoType),
        size: PropTypes.number
      }),
      cover_image: imageType,
      /* eslint camelcase: "off" */
      view_href: PropTypes.string.isRequired
    }).isRequired,
    imageSize: PropTypes.oneOf(['thumb', 'small', 'medium', 'large']),
    /* Minimum image dimensions that will be displayed, if image is smaller than it will not be displayed */
    minImageDimensions: dimensionsType
  };

  static defaultProps = {
    imageSize: 'thumb',
    minImageDimensions: { height: 50, width: 50 }
  };

  static getSortedImages = (message, imageSize, minImageDimensions) => {
    const { images, cover_image, teaser, videos } = message;
    const sizeKey = `${imageSize}_href`;

    let processedImages = [];
    if (images && images.items && images.items.length > 0) {
      processedImages = _.sortBy(images.items, [
        // sort images by whether or not they're the cover image
        img => cover_image && cover_image.id !== img.id,
        // sort images by whether or not they're in the teaser
        img => teaser && teaser.indexOf(img.id) === -1
      ]);
    }

    processedImages = _.uniqBy(processedImages, 'id');
    const prefilteredCount = processedImages.length;
    processedImages = _.filter(processedImages, sizeKey);

    if (prefilteredCount > processedImages.length) {
      log.warn(
        `Some message images are not able to be displayed due to missing the "${sizeKey}" property.`
      );
    }

    // Videos only support the thumb_href size, so we always get that size
    // and map it to the requested size
    const videoImages = _flow(
      _filter(video => video.thumb_href),
      _map(video => ({
        type: 'image',
        id: `video-${video.id}`,
        [sizeKey]: video.thumb_href
      }))
    )(videos.items);

    const allImages = processedImages.concat(videoImages);

    // filter out images that are less than the specified size
    return _.filter(
      allImages,
      i => i.height >= minImageDimensions.height && i.width >= minImageDimensions.width
    );
  };

  render() {
    const { message, imageSize, minImageDimensions } = this.props;
    const images = MessageImages.getSortedImages(message, imageSize, minImageDimensions);

    if (images.length > 0) {
      return (
        <ImageSlider images={images} href={message.view_href} displayLimit={2} imageSize="thumb" />
      );
    } else {
      log.debug(`No message images found`);
      return null;
    }
  }
}

export default limuirsComponent(module, MessageImages);
